/** Lo que la API del Drive devuelve. */

export type Carpeta = {
  id: number
  nombre: string
  padre_id: number | null
  creado_por: number
  creado_en: string
  /**
   * Los roles que la abren, **ya resueltos** por el servidor: los suyos, o los del
   * ancestro más cercano que tenga.
   *
   * ⚠ La pantalla no reimplementa la herencia. Si lo hiciera, habría dos criterios
   * —el del servidor y el de aquí— y el día que discrepen ganaría el de aquí en lo
   * que se ve y el del servidor en lo que se puede abrir.
   */
  roles: number[]
  /** `true` si esos roles son suyos; `false` si los hereda. */
  permisos_propios: boolean
}

export type Version = {
  id: number
  numero: number
  mime: string
  bytes: number
  subido_por: number
  creado_en: string
}

export type Archivo = {
  id: number
  carpeta_id: number
  nombre: string
  creado_por: number
  creado_en: string
  /** De la más reciente a la más vieja. */
  versiones: Version[]
}

export type RolResumen = { id: number; nombre: string }

/** Un nodo del árbol, con sus hijas ya colgadas. */
export type Nodo = Carpeta & { hijas: Nodo[] }

/**
 * Cuelga cada carpeta de su madre.
 *
 * ⚠ **Una carpeta cuya madre no está en la lista se cuelga de la raíz**, no se
 * pierde. Pasa de verdad: quien administra ve «RRHH/Nómina» y no «RRHH», o al revés
 * —la madre abierta y la hija reservada—, porque el permiso es de cada carpeta. Sin
 * este arreglo, la carpeta a la que sí se tiene acceso desaparecería de la pantalla
 * por culpa de otra a la que no.
 */
export function armarArbol(carpetas: Carpeta[]): Nodo[] {
  const porId = new Map<number, Nodo>(carpetas.map((c) => [c.id, { ...c, hijas: [] }]))
  const raices: Nodo[] = []
  for (const nodo of porId.values()) {
    const madre = nodo.padre_id === null ? undefined : porId.get(nodo.padre_id)
    if (madre) madre.hijas.push(nodo)
    else raices.push(nodo)
  }
  return raices
}

export function kilobytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}
